import { Platform } from 'react-native';
import type { Blueprint } from './types';

export const PENDING_PREVIEW_KEY = 'smartsaas_pending_preview';

/** Matches the backend preview record TTL; older previews can no longer be claimed. */
const PENDING_PREVIEW_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type PendingPreview = {
  previewId: string;
  prompt: string;
  blueprint: Blueprint;
  createdAt: number;
};

function storage(): Storage | null {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return null;
  return sessionStorage;
}

function read(): PendingPreview | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(PENDING_PREVIEW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPreview;
    if (!parsed?.previewId || !parsed.blueprint) return null;
    if (Date.now() - (parsed.createdAt || 0) > PENDING_PREVIEW_MAX_AGE_MS) {
      store.removeItem(PENDING_PREVIEW_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function stashPendingPreview(entry: Omit<PendingPreview, 'createdAt'>): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(PENDING_PREVIEW_KEY, JSON.stringify({ ...entry, createdAt: Date.now() }));
  } catch {
    /* best-effort */
  }
}

/** Reuse a preview already generated for this exact idea (refresh / back navigation). */
export function peekPendingPreview(prompt?: string): PendingPreview | null {
  const entry = read();
  if (!entry) return null;
  if (prompt !== undefined && entry.prompt !== prompt.trim()) return null;
  return entry;
}

/** One-shot: the preview id to claim after sign-in. */
export function consumePendingPreviewId(): string | undefined {
  const entry = read();
  storage()?.removeItem(PENDING_PREVIEW_KEY);
  return entry?.previewId;
}

export function clearPendingPreview(): void {
  storage()?.removeItem(PENDING_PREVIEW_KEY);
}
